import { useState, useRef, useCallback, useEffect } from 'react';

interface UseCameraOptions {
  facingMode?: 'user' | 'environment';
}

interface UseCameraReturn {
  isOpen: boolean;
  stream: MediaStream | null;
  error: string | null;
  facingMode: 'user' | 'environment';
  videoRef: React.RefObject<HTMLVideoElement>;
  canvasRef: React.RefObject<HTMLCanvasElement>;
  openCamera: () => Promise<void>;
  closeCamera: () => void;
  capturePhoto: () => File | null;
  flipCamera: () => Promise<void>;
  hasMultipleCameras: boolean;
}

export function useCamera(options: UseCameraOptions = {}): UseCameraReturn {
  const [isOpen, setIsOpen] = useState(false);
  const [stream, setStream] = useState<MediaStream | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [facingMode, setFacingMode] = useState<'user' | 'environment'>(options.facingMode || 'environment');
  const [hasMultipleCameras, setHasMultipleCameras] = useState(false);
  
  const videoRef = useRef<HTMLVideoElement>(null);
  const canvasRef = useRef<HTMLCanvasElement>(null);

  // Check for multiple cameras
  useEffect(() => {
    const checkCameras = async () => {
      try {
        const devices = await navigator.mediaDevices.enumerateDevices();
        const videoDevices = devices.filter(d => d.kind === 'videoinput');
        setHasMultipleCameras(videoDevices.length > 1);
      } catch (err) {
        console.error('Could not enumerate devices:', err);
      }
    };
    checkCameras();
  }, []);

  const stopStream = useCallback(() => {
    if (stream) {
      stream.getTracks().forEach(track => track.stop());
      setStream(null);
    }
  }, [stream]);

  const openCamera = useCallback(async () => {
    setError(null);
    
    try {
      // Check if camera API is supported
      if (!navigator.mediaDevices?.getUserMedia) {
        throw new Error('Camera not supported on this device');
      }

      const mediaStream = await navigator.mediaDevices.getUserMedia({
        video: {
          facingMode,
          width: { ideal: 1920 },
          height: { ideal: 1080 },
        },
        audio: false,
      });

      setStream(mediaStream);
      setIsOpen(true);

      // Attach to video element
      if (videoRef.current) {
        videoRef.current.srcObject = mediaStream;
        await videoRef.current.play();
      }
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Camera access denied';
      setError(message);
      console.error('Camera error:', err);
    }
  }, [facingMode]);

  const closeCamera = useCallback(() => {
    stopStream();
    setIsOpen(false);
    setError(null);
  }, [stopStream]);

  const flipCamera = useCallback(async () => {
    stopStream();
    const newFacingMode = facingMode === 'user' ? 'environment' : 'user';
    setFacingMode(newFacingMode);
    
    try {
      const mediaStream = await navigator.mediaDevices.getUserMedia({
        video: {
          facingMode: newFacingMode,
          width: { ideal: 1920 },
          height: { ideal: 1080 },
        },
        audio: false,
      });

      setStream(mediaStream);

      if (videoRef.current) {
        videoRef.current.srcObject = mediaStream;
        await videoRef.current.play();
      }
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to flip camera';
      setError(message);
    }
  }, [facingMode, stopStream]);

  const capturePhoto = useCallback((): File | null => {
    if (!videoRef.current || !canvasRef.current) {
      return null;
    }

    const video = videoRef.current;
    const canvas = canvasRef.current;
    const context = canvas.getContext('2d');

    if (!context) {
      return null;
    }

    // Set canvas size to video size
    canvas.width = video.videoWidth;
    canvas.height = video.videoHeight;

    // Draw video frame to canvas
    context.drawImage(video, 0, 0, canvas.width, canvas.height);

    // Convert to blob and then to File
    const dataUrl = canvas.toDataURL('image/jpeg', 0.92);
    const blob = dataURLtoBlob(dataUrl);
    
    if (!blob) return null;

    const file = new File([blob], `aura-capture-${Date.now()}.jpg`, {
      type: 'image/jpeg',
    });

    return file;
  }, []);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      stopStream();
    };
  }, [stopStream]);

  return {
    isOpen,
    stream,
    error,
    facingMode,
    videoRef,
    canvasRef,
    openCamera,
    closeCamera,
    capturePhoto,
    flipCamera,
    hasMultipleCameras,
  };
}

// Helper to convert data URL to Blob
function dataURLtoBlob(dataUrl: string): Blob | null {
  try {
    const arr = dataUrl.split(',');
    const mimeMatch = arr[0].match(/:(.*?);/);
    if (!mimeMatch) return null;
    
    const mime = mimeMatch[1];
    const bstr = atob(arr[1]);
    let n = bstr.length;
    const u8arr = new Uint8Array(n);
    
    while (n--) {
      u8arr[n] = bstr.charCodeAt(n);
    }
    
    return new Blob([u8arr], { type: mime });
  } catch {
    return null;
  }
}
